/* eslint-disable react/prop-types */
import {
  Container,
  Grid,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Typography,
  Box,
  CardActions,
  Switch,
  Tooltip,
  useTheme,
  alpha
} from '@mui/material';
import React, { useEffect, useState, useRef } from 'react';
import { useParams, useHistory } from 'react-router';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import EditIcon from '@mui/icons-material/Edit';
import LockResetIcon from '@mui/icons-material/LockReset';
import { useDispatch, useSelector } from 'react-redux';
import Loader from 'src/common/Loader';
import ConfirmationDialog from 'src/common/ConfirmationDialog';
import { setSuccessInfo, setErrorInfo } from 'src/redux/modalSlice';
import { getApproverDetails, updateApprover } from 'src/redux/approverSlice';
import { getStateName } from 'src/utils/getInitials';
import { CONFIRMATION_MSG } from 'src/utils/constants';
import ROUTE from 'src/utils/routeConstants';
import PasswordResetModal from 'src/common/PasswordResetModal';
import NewApprover from './NewApprovers';

/* ---------- Info Block ---------- */
function InfoBlock({ label, value }) {
  const theme = useTheme();
  const textRef = useRef(null);
  const [showTooltip, setShowTooltip] = useState(false);

  useEffect(() => {
    if (textRef.current) {
      setShowTooltip(
        textRef.current.scrollWidth > textRef.current.clientWidth
      );
    }
  }, [value]);

  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        transition: 'all 0.2s',
        '&:hover': {
          borderColor: theme.palette.primary.main,
          bgcolor: alpha(theme.palette.primary.main, 0.02)
        }
      }}
    >
      <Typography
        variant="caption"
        color="text.secondary"
        fontWeight={600}
        sx={{
          textTransform: 'uppercase',
          letterSpacing: 0.5,
          mb: 0.5
        }}
      >
        {label}
      </Typography>

      <Tooltip title={showTooltip ? value : ''} arrow>
        <Typography
          ref={textRef}
          variant="body1"
          fontWeight={600}
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
        >
          {value || '--'}
        </Typography>
      </Tooltip>
    </Box>
  );
}

function ApproverDetails() {
  const theme = useTheme();
  const { userId } = useParams();
  const history = useHistory();
  const dispatch = useDispatch();

  const { loading, approverDetails } = useSelector(
    (state) => state.approvers
  );

  const [openModal, setOpenModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showPassResetModal, setShowPassResetModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    dispatch(getApproverDetails(userId));
  }, [dispatch, userId]);

  const updateStatus = async () => {
    setShowConfirmModal(false);
    setIsLoading(true);

    try {
      const values = {
        ...approverDetails,
        is_active: !approverDetails.is_active
      };

      await dispatch(updateApprover(values)).unwrap();

      dispatch(
        setSuccessInfo({
          successMsg: 'Approver status updated successfully!'
        })
      );
    } catch (error) {
      dispatch(
        setErrorInfo({
          errorMsg: error?.error_message
        })
      );
    }

    setIsLoading(false);
  };

  if (loading || isLoading) return <Loader />;

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <NewApprover
        open={openModal}
        initialValues={approverDetails}
        handleClose={() => setOpenModal(false)}
      />

      <Card
        sx={{
          borderRadius: 3,
          boxShadow: '0 4px 24px 0 rgba(0,0,0,0.05)',
          border: `1px solid ${theme.palette.divider}`,
          display: 'flex',
          flexDirection: 'column',
          maxHeight: 'calc(100vh - 140px)'
        }}
      >
        <CardHeader
          sx={{
            p: 3,
            borderBottom: `1px solid ${theme.palette.divider}`,
            position: 'sticky',
            top: 0,
            zIndex: 3,
            bgcolor: 'background.paper'
          }}
          title={(
            <Box>
              <Typography variant="h6" fontWeight={700}>
                APPROVER DETAILS
              </Typography>
              <Typography variant="body2" color="text.secondary">
                View and manage approver configuration
              </Typography>
            </Box>
          )}
          action={(
            <Button
              variant="outlined"
              startIcon={<ArrowBackIosNewIcon />}
              onClick={() => history.replace(ROUTE.approvers)}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                color: 'text.secondary',
                borderColor: 'divider',
                '&:hover': {
                  bgcolor: alpha(theme.palette.primary.main, 0.04),
                  borderColor: theme.palette.primary.main,
                  color: theme.palette.primary.main
                }
              }}
            >
              Back
            </Button>
          )}
        />

        <CardContent sx={{ p: 4, flex: 1, overflowY: 'auto' }}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={4}>
              <InfoBlock label="Employee ID" value={approverDetails.emp_id} />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <InfoBlock
                label="Name"
                value={`${approverDetails.first_name} ${approverDetails.last_name}`}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <InfoBlock label="Site" value={approverDetails.site_name} />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <InfoBlock label="Email" value={approverDetails.email} />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <InfoBlock label="Phone" value={approverDetails.mobile_num} />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <InfoBlock
                label="Location"
                value={`${approverDetails.city || ''}, ${getStateName(
                  approverDetails.state
                )}`}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: `1px solid ${theme.palette.divider}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}
              >
                <Typography variant="caption" fontWeight={600}>
                  STATUS
                </Typography>

                <Switch
                  checked={approverDetails.is_active}
                  onChange={() => setShowConfirmModal(true)}
                  color="success"
                />
              </Box>
            </Grid>
          </Grid>
        </CardContent>

        <Divider />

        <CardActions sx={{
          p: 2.5, justifyContent: 'flex-end', gap: 2, position: 'sticky', bottom: 0, zIndex: 2, bgcolor: 'background.paper', borderTop: '1px solid', borderColor: 'divider'
        }}
        >
          <Button
            startIcon={<EditIcon />}
            variant="contained"
            onClick={() => setOpenModal(true)}
            sx={{
              borderRadius: 1.5,
              textTransform: 'none',
              fontWeight: 600
            }}
          >
            Update Details
          </Button>

          <Button
            startIcon={<LockResetIcon />}
            variant="contained"
            onClick={() => setShowPassResetModal(true)}
            sx={{
              borderRadius: 1.5,
              textTransform: 'none',
              fontWeight: 600
            }}
          >
            Reset Password
          </Button>
        </CardActions>
      </Card>

      <PasswordResetModal
        open={showPassResetModal}
        userEmail={approverDetails?.email}
        onClose={() => setShowPassResetModal(false)}
      />

      <ConfirmationDialog
        info={
          approverDetails.is_active
            ? CONFIRMATION_MSG.disableApprover
            : CONFIRMATION_MSG.enableApprover
        }
        open={showConfirmModal}
        disable={approverDetails.is_active}
        onClose={() => setShowConfirmModal(false)}
        onSubmit={updateStatus}
      />
    </Container>
  );
}

export default ApproverDetails;
